import { CheckOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Col, Flex, Grid, Row, Skeleton } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomMessage } from "../../../../components";
import { H6, P1, P2, P3 } from "../../../../components/NormalFonts";
import { useAuth } from "../../../../context/AuthContext";
import { useLayout } from "../../../../context/LayoutContext";
import apiErrorCode from "../../../../enumerations/apiErrorCode";
import { queryKey } from "../../../../enumerations/queryKey";
import { useAddToCart } from "../../../../hooks/Cart/cart.hooks";
import { AddCartParams } from "../../../../hooks/Cart/data";
import { useFacebookPixel } from "../../../../hooks/FbPixel/fbPixel.hooks";
import {
  SearchResponse,
  SearchSessionDetail,
} from "../../../../hooks/Search/data";
import useGetTimezone from "../../../../hooks/Timezone/timezone.hooks";
import { moneyFormat } from "../../../../plugins/numberFormat";
import { pushGtag, scrollToTop } from "../../../../plugins/utils";
import { steps } from "../../enumerations/steps";
import { SessionItem } from "./components";
import { SearchResultProps } from "./data";
import styles from "./style.module.scss";

const SearchResult: React.FC<SearchResultProps> = ({
  onChangeStep,
  searchResult = [],
  loadingResult = false,
  nickname,
  selectedItem,
  setSelectedItem,
}) => {
  const { t } = useTranslation();
  const { useBreakpoint } = Grid;
  const queryClient = useQueryClient();
  const { toLocalTimezone, formatDate } = useGetTimezone();
  const { getMargin } = useLayout();
  const { setLoginForm, setAuthModalVisible, isAuthenticated } = useAuth();
  const { isHavePixelIntegration, pixelCreateCustomTracker } =
    useFacebookPixel();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState<boolean>(false);
  const { mutateAsync: addToCart } = useAddToCart();

  const renderTitle = (item: SearchResponse) => {
    return (
      <div>
        <Flex align="center" justify="space-between">
          <H6
            style={{ marginBottom: 16, textWrap: "wrap" }}
            weight="medium"
            color="var(--text-content-1)"
          >
            {nickname
              ? t("normal.coach.nickname", { nickname })
              : `${formatDate(toLocalTimezone(item.start_date))} ~ ${formatDate(
                  toLocalTimezone(item.end_date)
                )}`}
          </H6>
          {screens.md ? (
            <Button
              type="primary"
              style={{
                height: 36,
                opacity:
                  selectedItem.length > 0 && !selectedItem.includes(item)
                    ? 0.5
                    : 1,
              }}
              className={
                selectedItem.length > 0 && selectedItem.includes(item)
                  ? styles.selected
                  : ""
              }
              onClick={() => {
                setSelectedItem([item]);
              }}
            >
              <P2 color="var(--Neutral-light-10)">
                {selectedItem.length > 0 && selectedItem.includes(item) ? (
                  <Flex align="center" gap={8}>
                    <CheckOutlined />
                    {t("normal.selected")}
                  </Flex>
                ) : (
                  t("normal.select")
                )}
              </P2>
            </Button>
          ) : null}
        </Flex>
        <Row gutter={[0, 10]}>
          <Col span={nickname ? (screens.md ? 8 : 12) : 12}>
            <Flex vertical gap={4}>
              <P2 color="var(--text-content-3)">{t("normal.skill.title")}</P2>
              <P1 weight="medium" color="var(--text-content-1)">
                {t(`normal.skill.${item.skill_code}`)}
              </P1>
            </Flex>
          </Col>
          <Col span={nickname ? (screens.md ? 8 : 12) : 12}>
            <Flex vertical gap={4}>
              <P2 color="var(--text-content-3)">{t("normal.count.title")}</P2>
              <P1 weight="medium" color="var(--text-content-1)">
                {t(`recordDetail.adults`, { count: item.adults })}
              </P1>
              <P1 weight="medium" color="var(--text-content-1)">
                {t(`recordDetail.kids`, { count: item.kids })}
              </P1>
            </Flex>
          </Col>
          {nickname ? (
            <Col span={nickname ? (screens.md ? 8 : 24) : 12}>
              <Flex vertical gap={4}>
                <P2 color="var(--text-content-3)">
                  {nickname
                    ? t("recordDetail.skiResort")
                    : t("home.searchbar.date.title")}
                </P2>
                <P1
                  weight="medium"
                  color="var(--text-content-1)"
                  style={{ textWrap: "wrap" }}
                >
                  {nickname
                    ? t(`skiresort.${item.ski_resort_code}`)
                    : `${formatDate(
                        toLocalTimezone(item.start_date)
                      )} ~ ${formatDate(toLocalTimezone(item.end_date))}`}
                </P1>
              </Flex>
            </Col>
          ) : null}
        </Row>
      </div>
    );
  };

  const renderBody = (item: SearchResponse) => {
    if (loadingResult)
      return (
        <Skeleton
          style={{ margin: 20 }}
          active
          title={{ width: "80%" }}
          paragraph={{ rows: 0, width: "60%" }}
        />
      );
    return (
      <div>
        {item.session_details.map(
          (session: SearchSessionDetail, index: number) => (
            <SessionItem
              key={`session_${index}`}
              data={session}
              nickname={nickname}
            />
          )
        )}
        <div className={styles.footer}>
          <P2 color="var(--text-content-3)">{t("search.summary")}</P2>
          <H6 weight="heavy" color="var(--primary)">{`${t(
            `currency.${process.env.REACT_APP_CURRENCY}`
          )} ${moneyFormat(item.total_price)}`}</H6>
        </div>
        {!screens.md ? (
          <div className={styles.footerButton}>
            <Button
              type="primary"
              style={{
                height: 36,
                width: "100%",
                opacity:
                  selectedItem.length > 0 && !selectedItem.includes(item)
                    ? 0.5
                    : 1,
              }}
              className={
                selectedItem.length > 0 && selectedItem.includes(item)
                  ? styles.selected
                  : ""
              }
              onClick={() => {
                setSelectedItem([item]);
              }}
            >
              <P2 color="var(--Neutral-light-10)">
                {selectedItem.length > 0 && selectedItem.includes(item) ? (
                  <Flex align="center" gap={8}>
                    <CheckOutlined />
                    {t("normal.selected")}
                  </Flex>
                ) : (
                  t("normal.select")
                )}
              </P2>
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  const handleGoToCheckout = () => {
    if (isAuthenticated === false) {
      setAuthModalVisible(true);
      return;
    }

    if (selectedItem.length === 0) {
      CustomMessage({
        alertType: "error",
        alertMessage: t("cart.add.error"),
      });
      return;
    }

    pushGtag({
      event: "go_to_checkout",
    });

    onChangeStep(steps.PAY);
    scrollToTop("main-layout");
  };

  const handleGoToCart = async () => {
    if (isAuthenticated === false) {
      setAuthModalVisible(true);
      return;
    }
    setLoading(true);

    if (selectedItem.length === 0) {
      CustomMessage({
        alertType: "error",
        alertMessage: t("cart.add.error"),
      });
      return;
    }

    const request: AddCartParams = {
      company_code: process.env.REACT_APP_COMPANY_CODE,
      order_items: selectedItem,
    };
    try {
      await addToCart(
        {
          params: request,
        },
        {
          onSuccess: async (data: any) => {
            console.debug("data", data);

            pushGtag({
              event: "add_to_cart",
            });

            if (isHavePixelIntegration) {
              pixelCreateCustomTracker({
                eventId: "AddToCart",
                props: {
                  items: selectedItem,
                },
              });
            }

            setLoading(false);
            CustomMessage({
              alertType: "success",
              alertMessage: t("cart.add.success"),
            });
            queryClient.invalidateQueries({ queryKey: [queryKey.CART_STATUS] });
          },
          onError: (error: any) => {
            if (
              error.data.result.code === apiErrorCode.TOKEN_ERROR ||
              error.data.result.code === apiErrorCode.AUTH_FAIELD_ERROR
            ) {
              setAuthModalVisible(true);
            }
            CustomMessage({
              alertMessage: t([
                `error.${error.data.result.code}`,
                error.data.result.message,
              ]),
              alertType: "error",
            });
            setLoading(false);
          },
        }
      );
    } catch (error) {
      console.debug("error add to cart", error);
    }
  };

  return (
    <div
      className={`${styles.searchResultContainer} ${
        screens.sm ? "" : styles.mobile
      }`}
    >
      <P3 color="var(--text-content-1)">{t("search.results.title")}</P3>
      {searchResult.map((item: SearchResponse, index: number) => (
        <Card
          style={{
            width: "100%",
            border: selectedItem.includes(item)
              ? "1px solid var(--secondary)"
              : "none",
          }}
          styles={{ body: { paddingLeft: 0, paddingRight: 0 } }}
          size={screens.sm ? "default" : "small"}
          title={renderTitle(item)}
          key={`search_result_${index}`}
        >
          {renderBody(item)}
        </Card>
      ))}
      <div
        className={`${styles.buttonContainer} ${
          screens.sm ? "" : styles.mobile
        }`}
        // style={{ marginLeft: getMargin() }}
      >
        <Flex
          align="center"
          justify="center"
          gap={4}
          style={{ padding: screens.md ? "0 30%" : 0 }}
        >
          <Button
            onClick={handleGoToCart}
            className={styles.addCart}
            size={"small"}
          >
            {t("normal.addcart")}
          </Button>
          <Button
            onClick={handleGoToCheckout}
            className={styles.addCheckout}
            type="primary"
            size={"small"}
          >
            {t("normal.gocheckout")}
          </Button>
        </Flex>
      </div>
      <div style={{ height: screens.sm ? 48 : 38 }} />
    </div>
  );
};

export default SearchResult;
